/**
 * "A new version is available" — V5-02 §2.4.
 *
 * A stale service worker serving yesterday's bundle has broken this project
 * about four times, and every time it was reported as a feature not working.
 * The cost is not the caching — it is that a cached shell is indistinguishable
 * from a bug, so hours go into the wrong investigation.
 *
 * `GET /version` returns a hash of the deployed `index.html`. We record what it
 * said when this tab loaded and compare on focus. A change means the server is
 * serving a build this tab is not running, which is exactly the stale case.
 *
 * In development the server returns a constant, so this never fires.
 */

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

/** Focus fires constantly while alt-tabbing; one check a minute is plenty. */
const THROTTLE_MS = 60_000;

async function fetchBuild(): Promise<string | null> {
  try {
    return (await api.get<{ build: string }>('/version')).data.build;
  } catch {
    // Offline, or the API is restarting mid-deploy. Not worth telling anyone
    // about — the next focus will try again.
    return null;
  }
}

/**
 * Clears the service-worker caches and reloads.
 *
 * `skipWaiting` alone is not enough: a worker that is already activated keeps
 * serving its precache, so the shell has to go too or the reload lands on the
 * same stale bundle it was meant to escape.
 */
async function refreshToLatest(): Promise<void> {
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations?.();
    for (const registration of registrations ?? []) {
      registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
      await registration.update().catch(() => undefined);
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Nothing here is worth blocking the reload for.
  }
  window.location.reload();
}

export function useVersionCheck(): void {
  const loadedBuild = useRef<string | null>(null);
  const lastCheck = useRef(0);
  const prompted = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function check(): Promise<void> {
      const now = Date.now();
      if (now - lastCheck.current < THROTTLE_MS) return;
      lastCheck.current = now;

      const build = await fetchBuild();
      if (cancelled || !build || build === 'dev') return;

      if (loadedBuild.current === null) {
        loadedBuild.current = build;
        return;
      }
      if (build === loadedBuild.current || prompted.current) return;

      // Never auto-reload: someone may be mid-sentence in a task description,
      // and losing their words to fix a caching problem is a worse bug than
      // the one being fixed.
      prompted.current = true;
      toast('A new version is available', {
        id: 'version-mismatch',
        duration: Infinity,
        description: 'Refresh to pick up the latest changes.',
        action: { label: 'Refresh', onClick: () => void refreshToLatest() },
        onDismiss: () => {
          prompted.current = false;
        },
      });
    }

    void check();
    window.addEventListener('focus', check);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', check);
    };
  }, []);
}
